import React from 'react';
import {
  AlertDescription,
  Flex,
  Box,
  Button,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  Tooltip,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {BuilderCenteredContent} from './BuilderCenteredContent';
import {BuilderFooter} from './BuilderFooter';
import {useAgentBuilderNavStore} from './AgentBuilderNav.state';
import {AgentBuilderPageIndicator} from './AgentBuilderPageIndicator';
import {useAgentBuilderServerState} from './AgentBuilder.state';
import {useAgentAnalystField} from './helpers';
import {AiFieldDefinition, getFieldLabel} from '../../../shared/enrichment';
import {useIsAnalysisRunInProgress} from './hooks/useIsAnalysisRunInProgress';
import {SelectTestListsButton} from './SelectTestListsButton';
import {AnalysisRunRefreshBanner} from './AnalysisRunRefreshBanner';
import {DownloadAnalysisRunResultsButton} from './DownloadAnalysisRunResultsButton';
import {useLatestFieldDefinitionAnalysisRun} from '../../../hooks/api/fieldDefinitionAnalysisRun';
import {useGetTestLists} from '../../../hooks/api/testList';
import {AnalysisRunProvider} from './AnalysisRunProvider';
import {AnalysisResultsTable} from './AnalysisResultsTable';
import {formatRelative} from 'date-fns';
import {StepContainer} from './StepContainer';

export const AgentBuilderAnalyzeListsStep = () => {
  const agentId = useAgentBuilderServerState().id;
  const agentAnalystField = useAgentAnalystField(agentId);

  if (!agentAnalystField) {
    return (
      <Alert status="error">
        <AlertIcon />
        <Box>
          <AlertTitle>No analyst field found</AlertTitle>
          <AlertDescription>
            Please contact Keyplay support for help.
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  return (
    <_AgentBuilderAnalyzeListsStep agentAnalystField={agentAnalystField} />
  );
};

const _AgentBuilderAnalyzeListsStep = ({
  agentAnalystField,
}: {
  agentAnalystField: AiFieldDefinition;
}) => {
  const {prevStep, nextStep} = useAgentBuilderNavStore();

  const {
    data: fieldDefinitionAnalysisRun,
    isPending: isRunDetailsPending,
    isError: isRunDetailsError,
  } = useLatestFieldDefinitionAnalysisRun({
    fieldDefinitionId: agentAnalystField.id,
  });
  const {
    data: testLists,
    isPending: isTestListsPending,
    isError: isTestListsError,
  } = useGetTestLists({});

  const isPending = isRunDetailsPending || isTestListsPending;
  const isError = isRunDetailsError || isTestListsError;

  return (
    <StepContainer>
      <Flex direction="column" flex="1">
        <BuilderCenteredContent maxW="1200px">
          <AgentBuilderPageIndicator />
          <Flex direction="column" flex="1" gap="4">
            <HeaderSection agentAnalystField={agentAnalystField} />
            {isPending ? (
              <Flex justifyContent="center" py="10">
                <Spinner />
              </Flex>
            ) : isError ? (
              <Flex justifyContent="center" py="10">
                <Text color="kgray.300">Error loading analysis results.</Text>
              </Flex>
            ) : !fieldDefinitionAnalysisRun ? (
              <Flex justifyContent="center" py="10">
                <Text color="kgray.300">
                  No analysis has been run yet. Select one or more test lists to
                  start analyzing.
                </Text>
              </Flex>
            ) : (
              <AnalysisRunProvider
                analysisRun={fieldDefinitionAnalysisRun}
                testLists={testLists!}
              >
                <AnalysisRunRefreshBanner fieldDefinition={agentAnalystField} />
                <AnalysisResultsTable />
                <Flex
                  color="kgray.300"
                  fontSize="sm"
                  justifyContent="end"
                  pb="4"
                >
                  Updated{' '}
                  {formatRelative(
                    fieldDefinitionAnalysisRun.timestamp,
                    new Date()
                  )}
                </Flex>
              </AnalysisRunProvider>
            )}
          </Flex>
        </BuilderCenteredContent>

        <Divider />

        <BuilderFooter justifyContent="space-between">
          <Button variant="ghost" onClick={prevStep}>
            Back
          </Button>

          <Button colorScheme="kbuttonblue" onClick={nextStep}>
            Continue to Review
          </Button>
        </BuilderFooter>
      </Flex>
    </StepContainer>
  );
};

const HeaderSection = ({
  agentAnalystField,
}: {
  agentAnalystField: AiFieldDefinition;
}) => {
  const isAnalysisRunInProgress = useIsAnalysisRunInProgress({
    fieldDefinitionId: agentAnalystField.id,
  });

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Flex alignItems="center" fontSize="xl" gap="2">
        <Box>Test Your Analyst</Box>
        <Box color="kgray.300">{getFieldLabel(agentAnalystField)}</Box>
      </Flex>
      <Flex alignItems="center" gap="2">
        {isAnalysisRunInProgress && (
          <Tooltip label="Analysis run in progress." placement="top">
            <Spinner color="kred.300" size="md" speed="1s" mr="4" />
          </Tooltip>
        )}
        <SelectTestListsButton fieldDefinition={agentAnalystField} />
        <DownloadAnalysisRunResultsButton fieldDefinition={agentAnalystField} />
      </Flex>
    </Flex>
  );
};
